/**
 * Утилиты для работы с localStorage с поддержкой TTL (Time To Live).
 */

interface StorageItem<T> {
  value: T
  expiresAt: number
}

/**
 * Сохраняет значение в localStorage с TTL.
 * 
 * @param key - Ключ для хранения
 * @param value - Значение для сохранения
 * @param ttlSeconds - Время жизни в секундах
 */
export function setWithTTL<T>(key: string, value: T, ttlSeconds: number): void {
  if (typeof window === 'undefined') return
  
  const expiresAt = Date.now() + (ttlSeconds * 1000)
  const item: StorageItem<T> = {
    value,
    expiresAt,
  }
  
  try {
    localStorage.setItem(key, JSON.stringify(item))
  } catch (error) {
    console.error(`[Storage] Ошибка сохранения ${key}:`, error)
  }
}

/**
 * Получает значение из localStorage с проверкой TTL.
 * 
 * @param key - Ключ для получения
 * @returns Значение или null, если не найдено или истек срок
 */
export function getWithTTL<T>(key: string): T | null {
  if (typeof window === 'undefined') return null
  
  try {
    const itemStr = localStorage.getItem(key)
    if (!itemStr) return null
    
    const item: StorageItem<T> = JSON.parse(itemStr)
    
    // Проверяем, не истек ли срок
    if (Date.now() > item.expiresAt) {
      localStorage.removeItem(key)
      return null
    }
    
    return item.value
  } catch (error) {
    console.error(`[Storage] Ошибка получения ${key}:`, error)
    // Удаляем поврежденные данные
    localStorage.removeItem(key)
    return null
  }
}

/**
 * Удаляет значение из localStorage.
 * 
 * @param key - Ключ для удаления
 */
export function removeWithTTL(key: string): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(key)
}

/**
 * Проверяет, существует ли значение и не истек ли срок.
 * 
 * @param key - Ключ для проверки
 * @returns true, если значение существует и не истек срок
 */
export function hasWithTTL(key: string): boolean {
  return getWithTTL(key) !== null
}

